import {
  faCircleCheck,
  faCircleSmall,
  faCircleXmark,
} from '@/components/app/icons';
import { cn } from '@/components/ui/cn';
import { SvgIcon as AppSvgIcon } from '@/components/ui/icon';
import { Stack } from '@/components/ui/layout';
import { List, ListItem } from '@/components/ui/list';
import { Text, type TextColor } from '@/components/ui/text';

import {
  PASSWORD_ALLOWED_SPECIAL_CHARACTERS,
  PASSWORD_MIN_LENGTH,
  PASSWORD_RESTRICTED_PATTERN_EXAMPLES,
  getPasswordRuleChecks,
} from '../utils/password/validate-password';

type PasswordRequirementStatus = 'error' | 'met' | 'pending';

const PASSWORD_REQUIREMENT_ITEMS = [
  {
    description: `Be at least ${PASSWORD_MIN_LENGTH} characters long`,
    id: 'minimum-length',
    isMet: (password: string) =>
      getPasswordRuleChecks(password).hasMinimumLength,
  },
  {
    description: 'Include uppercase, lowercase, and numeric characters',
    id: 'character-mix',
    isMet: (password: string) => {
      const checks = getPasswordRuleChecks(password);

      return checks.hasUppercase && checks.hasLowercase && checks.hasNumeric;
    },
  },
  {
    description: `Include 1 allowed special character: [${PASSWORD_ALLOWED_SPECIAL_CHARACTERS}]`,
    id: 'special-character',
    isMet: (password: string) =>
      getPasswordRuleChecks(password).hasAllowedSpecialCharacter,
  },
  {
    description: `Not contain restricted phrases such as '${PASSWORD_RESTRICTED_PATTERN_EXAMPLES.join(
      "', '"
    )}'`,
    id: 'restricted-patterns',
    isMet: (password: string) =>
      !getPasswordRuleChecks(password).hasRestrictedPatterns,
  },
  {
    description: 'Not include tab or newline characters',
    id: 'whitespace',
    isMet: (password: string) =>
      !getPasswordRuleChecks(password).hasInvalidWhitespace,
  },
] as const;

function getPasswordRequirementStatus({
  isMet,
  revealValidationState,
}: {
  isMet: boolean;
  revealValidationState: boolean;
}): PasswordRequirementStatus {
  if (isMet) {
    return 'met';
  }

  return revealValidationState ? 'error' : 'pending';
}

function getPasswordsMatchStatus({
  confirmPassword,
  password,
  revealValidationState,
}: {
  confirmPassword: string;
  password: string;
  revealValidationState: boolean;
}): PasswordRequirementStatus {
  if (!password && !confirmPassword) {
    return revealValidationState ? 'error' : 'pending';
  }

  if (!confirmPassword) {
    return revealValidationState ? 'error' : 'pending';
  }

  return password === confirmPassword ? 'met' : 'error';
}

/*
  `textColor` is a house `Text` colour KEY. These were MUI palette paths
  ('success.main', 'error.main', 'text.secondary') fed to `Typography`; the house
  Text indexes a closed set, so an unknown path resolves to no class at all and
  the description silently loses its status ink (and does not type-check). The
  equivalents are success / error / muted; `error` is the AA-safe
  destructive-text ink, while the glyph keeps the fill-stop `text-destructive`.
*/
function getRequirementPresentation(status: PasswordRequirementStatus): {
  glyphClassName: string;
  icon: typeof faCircleCheck;
  textColor: TextColor;
} {
  switch (status) {
    case 'met':
      return {
        textColor: 'success',
        glyphClassName: 'text-success',
        icon: faCircleCheck,
      };
    case 'error':
      return {
        textColor: 'error',
        glyphClassName: 'text-destructive',
        icon: faCircleXmark,
      };
    default:
      return {
        textColor: 'muted',
        glyphClassName: 'text-muted-foreground',
        icon: faCircleSmall,
      };
  }
}

function PasswordRequirementListItem({
  description,
  status,
  testId,
}: {
  description: string;
  status: PasswordRequirementStatus;
  testId: string;
}) {
  const presentation = getRequirementPresentation(status);

  return (
    <ListItem
      data-rule-status={status}
      data-testid={testId}
      // The house ListItem pads px-4 py-2; the MUI one was `disableGutters` plus
      // `p-0`, so only the padding reset is carried (twMerge lets p-0 win).
      className="p-0"
    >
      <Stack align="start" direction="row" spacing={1.25}>
        <AppSvgIcon
          aria-hidden
          icon={presentation.icon}
          className={cn(
            'mt-[calc(var(--minerva-spacing)*0.125)] text-[18px]',
            presentation.glyphClassName
          )}
        />
        <Text
          className="leading-[1.5]"
          color={presentation.textColor}
          variant="subtitle2"
        >
          {description}
        </Text>
      </Stack>
    </ListItem>
  );
}

/**
 * Expose the individual password policy checks as live status rows so public
 * auth flows can guide users before submit while still showing error states
 * once they try to continue with unmet requirements.
 */
export function PasswordRequirementChecklist({
  confirmPassword,
  password,
  revealValidationState,
}: {
  confirmPassword?: string;
  password?: string;
  revealValidationState: boolean;
}) {
  const normalizedPassword = password ?? '';
  const normalizedConfirmPassword = confirmPassword ?? '';
  const matchStatus = getPasswordsMatchStatus({
    confirmPassword: normalizedConfirmPassword,
    password: normalizedPassword,
    revealValidationState,
  });

  return (
    <Stack spacing={1}>
      <Text variant="subtitle2" as="p">
        Your password must meet all of the following:
      </Text>

      {/* The house List is a real <ul> already (the MUI one needed component="ul"). */}
      <List className="p-0">
        {PASSWORD_REQUIREMENT_ITEMS.map((item) => (
          <PasswordRequirementListItem
            key={item.id}
            description={item.description}
            status={getPasswordRequirementStatus({
              isMet: item.isMet(normalizedPassword),
              revealValidationState,
            })}
            testId={`password-requirement-${item.id}`}
          />
        ))}

        <PasswordRequirementListItem
          description="Match the confirmation password"
          status={matchStatus}
          testId="password-requirement-password-match"
        />
      </List>
    </Stack>
  );
}
